'use client';

import { IndividualDetails } from '@verifime/components';
import { ReactElement, ReactNode } from 'react';
import { useCustomerPermission } from './useCustomerPermission';

export type TIndividualDetailsViewProps = {
  customerId?: string;
  /** Whose permission to show. Required: see the hook for why this is load-bearing. */
  organisationId: string;
  /** Full name as one string, already assembled by the caller from what the customer told us. */
  name?: string;
  /** Already formatted, so every date on the card agrees on a format. */
  dateOfBirth?: string;
  email?: string;
  preferredContactNumber?: string;
  address?: ReactNode;
  /**
   * Draws the five identity values as placeholders while the **customer** is being fetched.
   *
   * The page's to set, not this view's, and deliberately separate from the permission's own loading
   * state. The five details arrive with the customer the page already fetches; the permission comes
   * from a call this view makes. They do not resolve together, so one flag would either skeleton
   * details the page has in memory or show a permission that has not arrived.
   */
  isLoadingDetails?: boolean;
  onError?: (error: unknown) => void;
  'data-testid'?: string;
};

/**
 * The customer details card with its permission fetched.
 *
 * **Only the permission is fetched here.** The identity details are passed in, because the page
 * already holds the customer and fetching it again would let two copies disagree while one is in
 * flight. The permission is different: it is a separate record, on a separate endpoint, that the
 * old page fetched separately too.
 *
 * **Three permission states, not two.** An earlier version read only `permission` off the hook and
 * dropped its `status`, which made a request in flight and a customer who genuinely holds no
 * permission render identically: the field simply absent. That reasoning was half right. Showing an
 * empty row would indeed be a false claim, but showing nothing claims "this organisation holds no
 * permission for this customer", which is equally a fact about their relationship and equally
 * unsupported while the call is open. It also made the card grow a sixth field when the permission
 * landed.
 *
 * So: `loading` renders the block as a placeholder, holding the field's footprint; `ready` with no
 * record omits the field, which is the one case where absence is the true statement; and a failure
 * omits it too, because a failed lookup is not evidence of a permission either way and the error is
 * reported through `onError`.
 */
export default function IndividualDetailsView({
  customerId,
  organisationId,
  onError,
  'data-testid': dataTestId,
  ...details
}: Readonly<TIndividualDetailsViewProps>): ReactElement {
  const { status, permission } = useCustomerPermission({ customerId, organisationId, onError });

  return (
    <IndividualDetails
      {...details}
      permission={status === 'loading' ? { status: 'loading' } : permission}
      data-testid={dataTestId}
    />
  );
}
